import React from 'react';
import styled from '@emotion/styled';
import type { AppTheme } from '@/theme/index';

export type MenuItemVariation = 'default' | 'account' | 'notification' | 'settings';

interface MenuItemProps {
  icon?: string | React.ReactNode;
  label: string;
  isActive?: boolean;
  isExpanded?: boolean;
  hasChildren?: boolean;
  level?: number;
  isOpen?: boolean;
  onClick?: () => void;
  theme: AppTheme;
  variation?: MenuItemVariation;
  badge?: number;
  parentLabel?: string;
}

const MenuItemWrapper = styled.div`
  position: relative;
`;

const StyledMenuItem = styled.div<{
  isActive?: boolean;
  isOpen?: boolean;
  theme: AppTheme;
  variation?: MenuItemVariation;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  padding: ${({ theme }) => theme.spacing[4]}px ${({ theme }) => theme.spacing[5]}px;
  gap: 8px;
  cursor: pointer;
  background: ${props =>
    props.isActive ? props.theme.colors.surface.interactive.selectedNav : 'transparent'};
  transition: background 0.3s ease-out;
  white-space: nowrap;
  overflow: hidden;
  justify-content: ${props => (props.isOpen ? 'flex-start' : 'center')};

  &:hover {
    background: ${({ theme }) => theme.colors.surface.interactive.hover};
  }

  ${props =>
    props.variation === 'settings' &&
    `
    svg, img {
      filter: brightness(0) saturate(100%) invert(47%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(88%);
    }
  `}

  color: ${props => (props.variation === 'settings' ? 'rgba(119, 119, 119, 1)' : 'inherit')};
`;

const Tooltip = styled.div<{ isOpen?: boolean; theme: AppTheme }>`
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 12px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.surface.layout.container};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: 6px;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid ${({ theme }) => theme.colors.border.divider};

  ${MenuItemWrapper}:hover & {
    opacity: ${props => (props.isOpen ? 0 : 1)};
    visibility: ${props => (props.isOpen ? 'hidden' : 'visible')};
  }
`;

const IconWrapper = styled.div<{ variation?: MenuItemVariation; isDark?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  ${props =>
    props.variation === 'account' &&
    `
    background: ${props.theme.colors.surface.group.brand};
    border-radius: 50%;
    padding: 4px;
  `}

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    ${props =>
      props.variation === 'account' &&
      `
      filter: brightness(0) saturate(100%) invert(100%);
    `}
    ${props =>
      props.variation !== 'account' &&
      props.variation !== 'settings' &&
      props.isDark &&
      `
      filter: brightness(0) invert(1);
    `}
  }

  svg {
    width: 100%;
    height: 100%;
    ${props =>
      props.variation !== 'account' &&
      props.variation !== 'settings' &&
      props.isDark &&
      `
      filter: brightness(0) invert(1);
    `}
  }
`;

const MenuText = styled.span<{ isOpen?: boolean; theme: AppTheme }>`
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.md}px;
  line-height: ${({ theme }) => theme.lineHeight.normal};
  letter-spacing: 0%;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  transition: opacity 0.3s ease-out;
  ${props => !props.isOpen && 'position: absolute; left: -9999px;'}
`;

const Chevron = styled.span<{ isExpanded?: boolean; isOpen?: boolean }>`
  margin-left: auto;
  transform: ${props => (props.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease-out;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  ${props => !props.isOpen && 'position: absolute; left: -9999px;'}
`;

const Badge = styled.span<{ isOpen?: boolean }>`
  margin-left: auto;
  background: ${({ theme }) => theme.colors.surface.group.brand};
  color: white;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  ${props => !props.isOpen && 'position: absolute; left: -9999px;'}
`;

export const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  label,
  isActive = false,
  isExpanded = false,
  hasChildren = false,
  isOpen = false,
  onClick,
  theme,
  variation = 'default',
  badge,
  parentLabel,
}) => {
  const renderIcon = () => {
    if (!icon) return null;

    if (typeof icon === 'string') {
      return (
        <IconWrapper variation={variation} isDark={theme.mode === 'dark'}>
          <img src={icon} alt="" />
        </IconWrapper>
      );
    }

    return <IconWrapper variation={variation} isDark={theme.mode === 'dark'}>{icon}</IconWrapper>;
  };

  return (
    <MenuItemWrapper>
      <StyledMenuItem
        isActive={isActive}
        isOpen={isOpen}
        theme={theme}
        onClick={onClick}
        variation={variation}
      >
        {renderIcon()}
        <MenuText isOpen={isOpen} theme={theme}>
          {label}
        </MenuText>
        {badge !== undefined && badge > 0 && <Badge isOpen={isOpen}>{badge}</Badge>}
        {hasChildren && (
          <Chevron isExpanded={isExpanded} isOpen={isOpen}>
            {'>'}
          </Chevron>
        )}
      </StyledMenuItem>
      <Tooltip isOpen={isOpen} theme={theme}>
        {parentLabel ? `${parentLabel} - ${label}` : label}
      </Tooltip>
    </MenuItemWrapper>
  );
};
